import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import actions from '../../../redux/actions';
import { RootState } from '../../../redux/store';
import { DataTable, PageContainer, PageSection } from '../../../ui';
import { colors } from '../../../theme/tokens';

/* Tester → Affiliates: opt in to get a promo code; customers who use it are linked to the tester */
const Affiliates: React.FC = () => {
  const dispatch = useDispatch();
  const { user, resolution } = useSelector((state: RootState) => state);
  const associatedCustomers: any[] = useSelector((state: any) => state.acssociatedCustomers) || [];
  const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user.affiliatePromo) {
      dispatch(actions.listAssociatedCustomers(user.id));
    }
  }, [user.affiliatePromo]);

  const copy = async () => {
    await navigator.clipboard.writeText(user.affiliatePromo);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <PageContainer isMobile={isMobile}>
      <PageSection title="How does the affiliate program work?" padding={isMobile ? 1.5 : 2.5}>
        <Box sx={{ '& p': { m: '0 0 10px' }, fontSize: 14, maxWidth: 820 }}>
          {!user.affiliatePromo && (
            <p>Click the button below to join. A unique promo code is generated for you right away, and you become an affiliate.</p>
          )}
          <p>Share your code with potential customers; to them it works like a discount coupon.</p>
          <p>
            When a customer uses your code for their first test cycle, they get a 10% discount, and their account is linked to yours. From then
            on, <strong>10%</strong> of the price of every test cycle they run goes to your account.
          </p>
        </Box>

        {!user.affiliatePromo && (
          <Button variant="contained" size="large" onClick={() => dispatch(actions.registerAffiliate(user.id))} sx={{ mt: 1 }}>
            Opt in
          </Button>
        )}
      </PageSection>

      {user.affiliatePromo && (
        <PageSection title="Your promo code" padding={isMobile ? 1.5 : 2.5}>
          <TextField
            value={user.affiliatePromo}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied' : 'Copy'}>
                    <IconButton aria-label="Copy promo code" onClick={copy} edge="end">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ width: isMobile ? '100%' : 320, mb: 3 }}
          />

          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1 }}>Your partners</Typography>
          <DataTable
            rows={associatedCustomers}
            rowKey={(row: any, i) => `${row.name}-${i}`}
            pageSize={20}
            emptyText="No customers have used your code yet"
            columns={[
              { key: 'name', label: 'Name', render: (row: any) => row.name },
              { key: 'joined', label: 'Joined on', render: (row: any) => moment(row.joinedDate).format('DD.MM.YYYY') },
              { key: 'revenue', label: 'Total revenue', align: 'right', render: (row: any) => `$${Number(row.totalRevenue || 0).toFixed(2)}` },
            ]}
          />
        </PageSection>
      )}
    </PageContainer>
  );
};

export default Affiliates;
